import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import useAuthStatus from "../../hooks/useAuthStatus";
import { getTransactionHistory, getUnreadCount, getUser } from "../../api/apiServices";
import { useAssets } from "../../hooks/useAssets";
import { useWallet } from "../../hooks/useWallet";
import { useTradingConfig } from "../../hooks/useTradingConfig";
import { useServices } from "../../hooks/useServices";
import { TxIconCell } from "../../utils/txIconHelper";
import { icons } from "../../utils/icons";
import ProfilePromptBanner from "../../components/feature/ProfilePromptBanner";
import MarketingSlides from "../../components/feature/MarketingSlides";
import FloatingSupportButton from "../../components/feature/FloatingSupportButton";

const formatNaira = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 });
const Sk = ({ className = "" }) => <div className={`animate-pulse bg-skeleton rounded-xl ${className}`} />;

function BalanceSkeleton() {
  return (
    <div className="mx-4 mt-4 rounded-3xl px-5 pt-6 pb-5 mb-4 bg-surface border border-border"
      style={{ boxShadow: "var(--shadow-balance-card)" }}>
      <div className="flex items-center justify-between mb-4">
        <Sk className="h-3 w-28" />
        <Sk className="h-7 w-7 rounded-lg" />
      </div>
      <Sk className="h-9 w-44 rounded-xl mb-4" />
      <div className="h-px bg-border mb-4" />
      <div className="flex justify-between">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="items-center flex flex-col gap-1.5" style={{ flex: 1 }}>
            <Sk className="w-11 h-11 rounded-full" />
            <Sk className="h-2.5 w-10" />
          </div>
        ))}
      </div>
    </div>
  );
}

function GuestLanding({ onSignUp }) {
  const features = [
    { icon: "ri-phone-line",        tone: "success", title: "Pay Bills Instantly",  desc: "Airtime, data, electricity & cable TV in seconds" },
    { icon: "ri-exchange-line",     tone: "info",    title: "Get Bank Account",     desc: "Get a dedicated account number, withdraw anywhere" },
    { icon: "ri-building-2-line",   tone: "warning", title: "Business Tools",       desc: "Staff onboarding, payroll & company goals" },
    { icon: "ri-gift-line",         tone: "purple",  title: "Earn Rewards",         desc: "Cashback, streak bonuses & referral earnings" },
  ];
  const trust = [
    { icon: "ri-shield-check-line", text: "Bank-grade security" },
    { icon: "ri-customer-service-2-line", text: "24/7 support" },
    { icon: "ri-flashlight-line",   text: "Instant transactions" },
  ];
  return (
    <div className="px-5 pb-8 space-y-6">
      <div className="text-center py-6">
        <div className="w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4" style={{ background: "var(--gradient-hero)" }}>
          <i className="ri-wallet-3-line text-white text-2xl" />
        </div>
        <h2 className="text-primary font-bold text-2xl mb-2 font-display">
          Your All-in-One<br />Finance App
        </h2>
        <p className="text-muted text-sm leading-relaxed max-w-[280px] mx-auto">
          Pay bills, send money, manage your business, and earn rewards — all in one place. Join thousands of Nigerians already saving time and money.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {features.map(f => (
          <div key={f.title} className="bg-surface rounded-2xl p-4 border border-border shadow-card">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `var(--color-${f.tone}-light)`, boxShadow: `inset 0 0 0 1px var(--color-${f.tone}-ring)` }}>
              <i className={`${f.icon} text-lg`} style={{ color: `var(--color-${f.tone})` }} />
            </div>
            <p className="text-primary text-sm font-bold mb-1 font-display">{f.title}</p>
            <p className="text-muted text-xs leading-tight">{f.desc}</p>
          </div>
        ))}
      </div>
      <div className="bg-surface-muted rounded-2xl px-4 py-4 flex items-center justify-around">
        {trust.map(t => (
          <div key={t.text} className="flex flex-col items-center gap-1.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "var(--color-success-light)" }}>
              <i className={`${t.icon} text-sm`} style={{ color: "var(--color-success)" }} />
            </div>
            <p className="text-muted text-[10px] font-semibold text-center leading-tight">{t.text}</p>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <button onClick={onSignUp}
          className="w-full py-4 bg-brand hover:bg-brand-hover text-on-brand font-bold text-sm rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2">
          <i className="ri-user-add-line" />Create Free Account
        </button>
        <button onClick={onSignUp}
          className="w-full py-3.5 bg-surface border border-border-strong text-secondary font-semibold text-sm rounded-2xl transition-all active:scale-95 hover:bg-surface-hover">
          Sign In to Existing Account
        </button>
      </div>
    </div>
  );
}

// ── Primary Actions — MATCHES RN's PRIMARY_ACTIONS exactly (same 4,
// same order, same intent), shown directly under the balance instead of
// web's previous separate "Quick Actions" chip-scroll section below the
// card. Icon set is Remix (web convention) rather than Ionicons (RN),
// but chosen to match RN's icon MEANING one-for-one.
const PRIMARY_ACTIONS = [
  { label: "Add Funds", icon: "ri-add-circle-line",     path: "/deposit-fiat",    primary: true },
  { label: "Send",      icon: "ri-send-plane-line",     path: "/internal-transfer" },
  { label: "Withdraw",  icon: "ri-arrow-up-circle-line", path: "/withdraw-naira" },
  { label: "Pay Link",  icon: "ri-links-line",          path: "/payment-link" },
];

// ── Services grid — MATCHES RN's SERVICES list exactly: same 7 items,
// same order, same labels/tones (Pay Link removed from here since it's
// now a Primary Action above, mirroring RN's own layout — RN doesn't
// duplicate Pay Link in both places either).
const ALL_SERVICES = [
  { label: "Airtime",     icon: "ri-phone-line",     tone: "success", path: "/bills/airtime" },
  { label: "Data",        icon: "ri-wifi-line",       tone: "info",    path: "/bills/data" },
  { label: "Electricity", icon: "ri-flashlight-line", tone: "warning", path: "/bills/electricity" },
  { label: "Cable TV",    icon: "ri-tv-2-line",       tone: "purple",  path: "/bills/tv" },
  { label: "Business",    icon: "ri-building-2-line", tone: "warning", path: "/business" },
  { label: "Savings",     icon: "ri-safe-line",       tone: "success", path: "/savings" },
  { label: "More",        icon: "ri-grid-line",       tone: null,      path: "/more" },
];

export default function Home() {
  const navigate        = useNavigate();
  const isAuthenticated = useAuthStatus();
  const { isEnabled }   = useServices();

  const [balanceVisible, setBalanceVisible] = useState(true);
  const [user,           setUser]           = useState(null);
  const [userLoading,    setUserLoading]    = useState(true);
  const [transactions,   setTransactions]   = useState([]);
  const [txLoading,      setTxLoading]      = useState(true);
  const [greeting,       setGreeting]       = useState("");
  const [unreadCount,    setUnreadCount]    = useState(0);
  const txInitialLoad = useRef(false);

  const { assets, priceMap, loading: ratesLoading } = useAssets({ pollInterval: isAuthenticated ? 60_000 : 0, enabled: isAuthenticated });
  const { nairaBalance, holdings, totalPortfolio, loading: walletLoading } =
    useWallet({ enabled: isAuthenticated, priceMap, pollInterval: 30_000 });

  const portfolioLoading = isAuthenticated && walletLoading;

  const tradingEnabled         = isEnabled("trading");
  const fiatDepositEnabled     = isEnabled("fiat_deposit");
  const nairaWithdrawalEnabled = isEnabled("naira_withdrawal");
  const depositsEnabled        = isEnabled("deposits");
  const { fees } = useTradingConfig({ enabled: isAuthenticated });
  const refReward = fees?.rewardOnReferral || 200;

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening");
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { setUserLoading(false); return; }
    getUser().then(d => { if (d?.status) setUser(d.user); }).catch(console.error).finally(() => setUserLoading(false));
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) { setTxLoading(false); return; }
    if (!txInitialLoad.current) setTxLoading(true);
    getTransactionHistory()
      .then(r => { if (r.status) setTransactions(r.data || []); })
      .catch(console.error)
      .finally(() => { setTxLoading(false); txInitialLoad.current = true; });
  }, [isAuthenticated]);

  const fetchUnread = useCallback(async () => {
    if (!isAuthenticated) return;
    try { const r = await getUnreadCount(); if (r.status) setUnreadCount(r.count); } catch {}
  }, [isAuthenticated]);
  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  // MATCHES RN: last 4 transactions, simple vertical list (not the
  // horizontal carousel this file used to have).
  const recentTxs = useMemo(() =>
    [...transactions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 2),
    [transactions]
  );

  return (
    <div className="min-h-screen bg-bg pb-28">

      <FloatingSupportButton user={user} />

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="bg-surface px-5 pt-12 pb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              userLoading ? (
                <><Sk className="w-11 h-11 rounded-full" /><div className="space-y-1.5"><Sk className="h-2.5 w-20" /><Sk className="h-3 w-28" /></div></>
              ) : (
                <>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden"
                    style={{ background: "var(--color-brand-light)", border: "2px solid var(--color-brand-border)" }}>
                    {user?.profileImage
                      ? <img src={user.profileImage} className="w-full h-full object-cover rounded-full" alt="" />
                      : <span className="font-bold text-base" style={{ color: "var(--color-brand-text)" }}>{user?.firstName?.charAt(0) || "?"}</span>}
                  </div>
                  <div>
                    <p className="text-muted text-xs">{greeting}</p>
                    <p className="text-primary font-bold text-sm font-display">{user?.firstName?.split(" ")[0]} 👋</p>
                  </div>
                </>
              )
            ) : (
              <div>
                <p className="text-primary font-bold text-lg font-display">Easwallet</p>
                <p className="text-muted text-xs">Nigeria's Smart Finance App</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <button onClick={() => navigate("/notifications")}
                className="relative w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center transition-colors">
                <i className="ri-notification-3-line text-secondary text-lg" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
            )}
            <button onClick={() => navigate("/support")} className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--color-warning-light)" }}>
              <i className="ri-customer-service-2-line text-lg" style={{ color: "var(--color-warning)" }} />
            </button>
            {!isAuthenticated && (
              <button onClick={() => navigate("/auth")} className="px-4 py-2 text-sm font-bold text-on-brand bg-brand rounded-xl hover:bg-brand-hover transition-colors">
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>

      {!isAuthenticated ? (
        <>
          <div className="px-5 pt-4"><MarketingSlides page="home" /></div>
          <GuestLanding onSignUp={() => navigate("/auth")} />
        </>
      ) : (
        <>
          <div className="px-5 pt-4"><ProfilePromptBanner user={user} loading={userLoading} /></div>

          {/* ── Balance Card + Primary Actions ────────────────────
              MATCHES RN exactly: label + eye toggle, big balance
              number, divider, then the 4 primary-action icons row
              (Add Funds/Send/Withdraw/Pay Link) directly inside the
              card. This REPLACES the previous separate "Add Funds"
              button + secured-wallet/history footer + the standalone
              "Quick Actions" chip-scroll section that used to sit
              below it — RN has one combined block, not two. */}
          {portfolioLoading ? <BalanceSkeleton /> : (
            <div className="mx-4 mt-4 mb-4 rounded-3xl relative overflow-hidden"
              style={{
                background: "var(--color-surface)",
                boxShadow: "var(--shadow-balance-card)",
                border: "1px solid var(--color-border)",
              }}>
              <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-3xl" style={{ background: "var(--gradient-hero)" }} />
              <div className="relative z-10 px-5 pt-6 pb-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted text-xs font-medium">Total Balance</span>
                  <button onClick={() => setBalanceVisible(v => !v)} className="w-7 h-7 rounded-lg bg-surface-muted flex items-center justify-center">
                    <i className={`${balanceVisible ? "ri-eye-line" : "ri-eye-off-line"} text-muted text-sm`} />
                  </button>
                </div>

                <h2 className="text-primary font-bold leading-none font-display" style={{ fontSize: "2.2rem", letterSpacing: "-0.5px", fontFeatureSettings: '"tnum"' }}>
                  {balanceVisible ? formatNaira(tradingEnabled ? totalPortfolio : nairaBalance) : "₦ ••••••"}
                </h2>

                {/* Action Row — matches RN's PRIMARY_ACTIONS mapping exactly */}
                <div className="flex justify-between mt-5 pt-4" style={{ borderTop: "1px solid var(--color-border)" }}>
                  {PRIMARY_ACTIONS.map(act => (
                    <button key={act.label} onClick={() => navigate(act.path)}
                      className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform" style={{ flex: 1 }}>
                      <div className="w-11 h-11 rounded-full flex items-center justify-center"
                        style={{ background: act.primary ? "var(--color-brand)" : "var(--color-surface-muted)" }}>
                        <i className={`${act.icon} text-lg`} style={{ color: act.primary ? "var(--color-text-on-brand)" : "var(--color-brand)" }} />
                      </div>
                      <span className="text-secondary text-[11px] font-semibold">{act.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Promotions & Offers ─────────────────────────────
              MATCHES RN position: directly after balance/actions,
              before the services grid. */}
          <div className="px-5"><MarketingSlides page="home" /></div>

          {/* ── Services grid — MATCHES RN's 7-item list/order ── */}
          <div className="px-5 mb-5 mt-5">
            <p className="text-primary font-bold text-sm font-display mb-3">Bills & Payments</p>
            <div className="flex flex-wrap gap-2.5">
              {ALL_SERVICES.map(s => (
                <button key={s.label} onClick={() => navigate(s.path)}
                  className="flex flex-col items-center gap-2 active:scale-95 transition-transform" style={{ width: "22%" }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{
                      background: s.tone ? `var(--color-${s.tone}-light)` : "var(--color-surface-muted)",
                      boxShadow: s.tone ? `inset 0 0 0 1px var(--color-${s.tone}-ring)` : "none",
                    }}>
                    <i className={`${s.icon} text-xl`} style={{ color: s.tone ? `var(--color-${s.tone})` : "var(--color-text-muted)" }} />
                  </div>
                  <span className="text-secondary text-[11px] font-semibold text-center" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                    {s.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          

          {/* ─────────────────────────────────────────────────────
              Everything below this point is NOT present in the RN
              HomeScreen shown — kept here rather than deleted since
              it looks like real functionality (crypto holdings, live
              rates, referrals), not styling. Say the word if these
              should come out entirely for a strict 1:1 match.
              ───────────────────────────────────────────────────── */}

          {/* ── Referral Banner ───────────────────────────────── */}
          {user?.referralCode && (
            <div className="px-5 mb-5">
              <div className="rounded-2xl px-4 py-4 relative overflow-hidden" style={{ background: "linear-gradient(135deg, var(--color-purple), #5B21B6)" }}>
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 85% 20%, rgba(255,255,255,0.1), transparent 60%)" }} />
                <div className="relative z-10 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-white text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm font-display">Refer & Earn ₦{refReward}</p>
                    <p className="text-white/60 text-xs mt-0.5">Share your code, earn on every signup</p>
                  </div>
                  <button
                    onClick={() => navigator.share?.({ title: "Join Easwallet", text: `Use my code ${user.referralCode}!` }).catch(() => navigator.clipboard.writeText(user.referralCode))}
                    className="flex-shrink-0 px-3 py-2 rounded-xl text-xs font-bold text-white"
                    style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.2)" }}>
                    Share
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Grow Your Money: Target Savings + Rewards ─────── */}
          <div className="px-5 mb-5">
            <h3 className="text-primary font-bold text-sm font-display mb-3">Grow Your Money</h3>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => navigate("/savings")}
                className="bg-surface rounded-2xl p-4 border border-border shadow-card text-left hover:border-brand-border active:scale-[0.98] transition-all">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: "var(--color-success-light)", boxShadow: "inset 0 0 0 1px var(--color-success-ring)" }}>
                  <i className="ri-safe-line text-lg" style={{ color: "var(--color-success)" }} />
                </div>
                <p className="text-primary text-sm font-bold font-display mb-1">Target Savings</p>
                <p className="text-muted text-xs leading-tight mb-3">Save toward a goal, earn daily interest</p>
                <span className="text-brand text-xs font-bold">Start saving →</span>
              </button>
              <button onClick={() => navigate("/rewards")}
                className="bg-surface rounded-2xl p-4 border border-border shadow-card text-left hover:border-brand-border active:scale-[0.98] transition-all">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: "var(--color-warning-light)", boxShadow: "inset 0 0 0 1px var(--color-warning-ring)" }}>
                  <i className="ri-gift-line text-lg" style={{ color: "var(--color-warning)" }} />
                </div>
                <p className="text-primary text-sm font-bold font-display mb-1">Rewards</p>
                <p className="text-muted text-xs leading-tight mb-3">Cashback on every bill, streak bonuses</p>
                <span className="text-brand text-xs font-bold">View rewards →</span>
              </button>
            </div>
          </div>

          {/* ── Recent Transactions — MATCHES RN's simple vertical
              list (icon circle + label/date + amount), replacing the
              previous horizontal-scroll card carousel design. Shows
              4 most recent, same as RN. ─────────────────────── */}
          <div className="px-5 mb-8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-primary font-bold text-sm font-display">Recent Transactions</p>
              <button onClick={() => navigate("/transactions")} className="text-brand text-xs font-semibold">See all</button>
            </div>

            <div className="bg-surface rounded-2xl border border-border shadow-card">
              {txLoading ? (
                [0, 1].map(i => (
                  <div key={i} className="flex items-center p-3.5" style={{ borderBottom: i === 0 ? "1px solid var(--color-border)" : "none" }}>
                    <Sk className="w-9 h-9 rounded-full mr-3" />
                    <div className="flex-1 space-y-1.5">
                      <Sk className="h-3 w-24" />
                      <Sk className="h-2.5 w-14" />
                    </div>
                  </div>
                ))
              ) : recentTxs.length === 0 ? (
                <div className="flex flex-col items-center py-6">
                  <i className="ri-receipt-line text-3xl text-faint" />
                  <p className="text-muted text-xs mt-2">No recent activity</p>
                </div>
              ) : (
                recentTxs.map((tx, i) => {
                  const isCredit = ["deposit", "buy", "internal_transfer_in", "reward_withdrawal"].includes(tx.type);
                  const label = tx.assetName || tx.type?.replace(/_/g, " ");
                  return (
                    <button key={tx._id || i} onClick={() => navigate(`/transaction/${tx._id}`)}
                      className="w-full flex items-center p-3 text-left active:scale-[0.99] transition-transform"
                      style={{ borderBottom: i < recentTxs.length - 1 ? "1px solid var(--color-border)" : "none" }}>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center mr-3 flex-shrink-0"
                        style={{ background: isCredit ? "var(--color-success-light)" : "var(--color-danger-light)" }}>
                        <i className={`${isCredit ? "ri-arrow-down-line" : "ri-arrow-up-line"} text-sm`}
                          style={{ color: isCredit ? "var(--color-success)" : "var(--color-danger)" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-primary font-semibold text-sm truncate capitalize">{label}</p>
                        <p className="text-muted text-[11px] mt-0.5">
                          {new Date(tx.createdAt).toLocaleDateString("en", { day: "numeric", month: "short" })}
                        </p>
                      </div>
                      <p className="font-bold text-xs flex-shrink-0" style={{ color: isCredit ? "var(--color-success)" : "var(--color-text-primary)" }}>
                        {isCredit ? "+" : "−"}{formatNaira(tx.nairaAmount)}
                      </p>
                    </button>
                  );
                })
              )}
            </div>
          </div>


          {/* ── Live Rates ────────────────────────────────────────── */}
          {tradingEnabled && (
            <div className="mb-5">
              <div className="flex items-center justify-between px-5 mb-3">
                <h3 className="text-primary font-bold text-sm font-display">Live Rates</h3>
                <button onClick={() => navigate("/buy")} className="text-brand text-xs font-medium">See all</button>
              </div>
              {ratesLoading ? (
                <div className="flex gap-3 px-5 overflow-x-auto pb-1">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex-shrink-0 bg-surface rounded-2xl p-3 min-w-[120px] border border-border shadow-card space-y-2">
                      <div className="flex items-center justify-between"><Sk className="h-3 w-8" /><Sk className="h-3 w-10" /></div>
                      <Sk className="h-2.5 w-16" /><Sk className="h-3 w-14" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex gap-3 px-5 overflow-x-auto pb-1 scrollbar-hide">
                  {assets.map(asset => (
                    <button key={asset.symbol} onClick={() => navigate("/buy")}
                      className="flex-shrink-0 bg-surface rounded-2xl p-3 flex flex-col gap-1 min-w-[120px] hover:bg-surface-hover transition-colors text-left border border-border shadow-card active:scale-95">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          {asset.image && <img src={asset.image} className="w-4 h-4 rounded-full object-contain" alt="" />}
                          <span className="text-primary text-xs font-bold">{asset.symbol}</span>
                        </div>
                        <span className="text-xs font-semibold"
                          style={{ color: asset.changeType === "positive" ? "var(--color-success)" : "var(--color-danger)" }}>
                          {asset.change24h >= 0 ? "+" : ""}{asset.change24h.toFixed(2)}%
                        </span>
                      </div>
                      <p className="text-muted text-[10px]">{asset.name}</p>
                      <p className="text-secondary text-xs font-semibold">{formatNaira(asset.priceNgn)}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Holdings ──────────────────────────────────────────── */}
          {tradingEnabled && (
            <div className="px-5 mb-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-primary font-bold text-sm font-display">My Holdings</h3>
                <button onClick={() => navigate("/wallet")} className="text-brand text-xs font-medium">View all</button>
              </div>
              {portfolioLoading ? (
                <div className="space-y-2">{[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-surface rounded-2xl px-4 py-3 flex items-center justify-between border border-border shadow-card">
                    <div className="flex items-center gap-3"><Sk className="w-10 h-10 rounded-full" /><div className="space-y-1.5"><Sk className="h-3 w-10" /><Sk className="h-2.5 w-16" /></div></div>
                    <div className="items-end flex flex-col gap-1.5"><Sk className="h-3 w-20" /><Sk className="h-2.5 w-10" /></div>
                  </div>
                ))}</div>
              ) : holdings.length > 0 ? (
                <div className="space-y-2">
                  {holdings.slice(0, 3).map(h => (
                    <div key={h.symbol} onClick={() => navigate("/wallet")}
                      className="bg-surface rounded-2xl px-4 py-3 flex items-center justify-between border border-border shadow-card cursor-pointer hover:border-brand-border active:scale-[0.99] transition-all">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" style={{ background: `${h.color}18`, boxShadow: `inset 0 0 0 1px ${h.color}30` }}>
                          {h.image ? <img src={h.image} alt={h.symbol} className="w-7 h-7 object-contain" /> : <i className={`${h.icon} text-lg`} style={{ color: h.color }} />}
                        </div>
                        <div><p className="text-primary text-sm font-bold">{h.symbol}</p><p className="text-muted text-xs">{h.totalBalance} {h.symbol}</p></div>
                      </div>
                      <div className="text-right">
                        <p className="text-primary text-sm font-bold">{formatNaira(h.valueNGN)}</p>
                        <p className="text-xs font-medium" style={{ color: h.change24h >= 0 ? "var(--color-success)" : "var(--color-danger)" }}>{h.change24h >= 0 ? "+" : ""}{h.change24h.toFixed(2)}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-surface rounded-2xl p-8 text-center border border-border shadow-card">
                  <div className="w-14 h-14 bg-surface-hover rounded-2xl flex items-center justify-center mx-auto mb-3"><i className="ri-wallet-3-line text-muted text-2xl" /></div>
                  <p className="text-secondary font-semibold text-sm mb-1">No crypto holdings yet</p>
                  <p className="text-muted text-xs mb-4">Deposit or buy crypto to get started</p>
                  {depositsEnabled && (
                    <button onClick={() => navigate("/deposit")} className="px-6 py-2.5 bg-brand text-on-brand font-semibold rounded-xl text-sm">Deposit Now</button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <MobileBottomNav />
    </div>
  );
}